import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '../api/queries';
import { isPlayerVerified, type User } from '../api/types';
import { useToast } from './Toast';

export function Layout({ user }: { user: User | null }) {
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();
  // Die AG-Planer-Karte füllt das ganze Browserfenster unterhalb der Kopfzeile (kein Container, kein Footer).
  const fullscreen = useLocation().pathname.startsWith('/ag-planer');
  const verified = user ? isPlayerVerified(user) : false;

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login', { replace: true }),
      onError: (error) => toast.push('error', error.message),
    });
  };

  return (
    <div className={fullscreen ? 'app-fullscreen' : 'app-shell'}>
      <header className="topbar">
        <NavLink to="/" className="brand">
          <span className="brand-mark" aria-hidden="true">
            ⚔
          </span>
          <span>TW-Tool</span>
        </NavLink>

        <nav className="nav">
          {user ? (
            <>
              <NavLink to="/" end>
                Portal
              </NavLink>
              {user.world && <span className="world-chip">{user.world.name}</span>}
              {/*
               * Ohne verifizierte Spielfigur bleibt der Rest des Portals
               * gesperrt (requireVerifiedPlayer serverseitig) -- die
               * Menüpunkte dafür tauchen dann gar nicht erst auf. Für jeden
               * neuen Menüpunkt gilt dieselbe Regel, außer ausdrücklich
               * anders entschieden.
               */}
              {verified && (
                <>
                  <NavLink to="/insert">Insert</NavLink>
                  <NavLink to="/berichte">Berichte</NavLink>
                  <NavLink to="/angriffe">Aktuelle Angriffe</NavLink>
                  <NavLink to="/dorftypen">Off-/Deff-Dörfer</NavLink>
                  <NavLink to="/abschickzeiten">Abschickzeiten</NavLink>
                  <NavLink to="/sd-anfragen">SD-Anfragen</NavLink>
                  <NavLink to="/retime-anfragen">Retime-Anfragen</NavLink>
                  <NavLink to="/ag-abfrage">AG-Abfrage</NavLink>
                  {(user.role === 'tribeLeader' || user.role === 'admin') && (
                    <>
                      <NavLink to="/verwaltung">Verwaltung</NavLink>
                      <NavLink to="/discord-einstellungen">Discord</NavLink>
                      <NavLink to="/angriffsplaner">Angriffsplaner</NavLink>
                      <NavLink to="/ag-planer">AG Planer</NavLink>
                    </>
                  )}
                  {user.role === 'admin' && (
                    <>
                      <NavLink to="/admin/welten">Welten</NavLink>
                      <NavLink to="/admin/benutzer">Benutzer</NavLink>
                      <NavLink to="/admin/userskript">Userskript</NavLink>
                    </>
                  )}
                </>
              )}
              <NavLink to="/konto">
                Konto
                {!verified && (
                  <span className="nav-badge" title="Spielfigur noch nicht verifiziert" aria-label="Achtung">
                    !
                  </span>
                )}
              </NavLink>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleLogout}
                disabled={logout.isPending}
              >
                {logout.isPending ? 'Abmelden …' : 'Abmelden'}
              </button>
            </>
          ) : (
            <>
              <NavLink to="/login">Anmelden</NavLink>
              <NavLink to="/register">Registrieren</NavLink>
            </>
          )}
        </nav>
      </header>

      {fullscreen ? (
        <main className="fullscreen-main">
          <Outlet />
        </main>
      ) : (
        <>
          <main className="container">
            <Outlet />
          </main>

          <footer className="footer">
            <span>TW-Tool — internes Portal</span>
          </footer>
        </>
      )}
    </div>
  );
}
